import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { RedeemQuoteMockEngine, type MockStudentSnapshot } from "./redeem-quote.mock";
import type { Prize } from "../services/prize.service";
import type {
  RedeemQuoteParams,
  RedeemQuoteResult,
  BatchRedeemResult,
} from "../services/redeem-record.service";

/**
 * dev 专用 mock 的根单例：持有开关 + 页面喂入的"当前奖品/学生快照"，供
 * 报价/兑换 http 拦截器应答。仅 dev（environment.redeemQuoteMock）生效；
 * 生产 environment 恒 false → enabled=false、不注册，绝不参与真实扣款。
 *
 * 用法（页面在发起 Quote/Redeem 前调用一次）：
 *   mock.arm(selectedPrize, students.map(s => ({ studentId: s.id, studentName: s.name, score: s.score })))
 * 关闭 mock（一键切真实后端）：mock.setEnabled(false) —— 之后请求透传给真实接口。
 * 未 arm（例如旧版兑换页直接 Redeem）→ canHandle=false → 拦截器放行真实后端，不回归。
 */
@Injectable({ providedIn: "root" })
export class RedeemQuoteMockService {
  private readonly engine = new RedeemQuoteMockEngine();

  /** mock 开关：dev 默认开；prod 恒 false */
  enabled = !environment.production && !!environment.redeemQuoteMock;

  /** 当前 mock 应答所依据的奖品快照（arm 时写入） */
  private prize: { id: number; pointsRequired: number; escalation?: Prize["escalation"] } | null = null;
  private students: MockStudentSnapshot[] = [];

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** 页面在发报价/兑换前，把"当前奖品 + 已选学生(含余额)"快照喂给 mock */
  arm(prize: Prize, students: MockStudentSnapshot[]): void {
    this.prize = {
      id: prize.id,
      pointsRequired: prize.pointsRequired,
      escalation: prize.escalation ?? null,
    };
    this.students = students;
  }

  /** 该奖品是否由本 mock 应答（开关开且已针对此奖品 arm） */
  canHandle(prizeId: number): boolean {
    return this.enabled && !!this.prize && this.prize.id === prizeId;
  }

  reset(): void {
    this.engine.reset();
    this.prize = null;
    this.students = [];
  }

  quote(params: RedeemQuoteParams): RedeemQuoteResult | null {
    if (!this.canHandle(params.prizeId) || !this.prize) return null;
    return this.engine.quote(this.prize, this.students, params);
  }

  redeem(params: RedeemQuoteParams): BatchRedeemResult | null {
    if (!this.canHandle(params.prizeId) || !this.prize) return null;
    return this.engine.redeem(this.prize, this.students, params);
  }
}
